import moment from 'moment';
import React from 'react';
import { HiOutlineX } from 'react-icons/hi';
import { ActionIcon, Badge, Modal } from 'rizzui';
import RefuelDocumentsViewer from '../../pages/refuels/components/RefuelDocumentsViewer';
import { Refuel } from '../../type/entities/refuel-type';

interface RefuelDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    refuel: Refuel | null;
}

const RefuelDetailModal: React.FC<RefuelDetailModalProps> = ({
    isOpen,
    onClose,
    refuel
}) => {
    if (!refuel) return null;

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    const formatDate = (date: any) => {
        if (!date) return 'Sin fecha';
        const dateValue = date?.date || date;
        return moment(dateValue).format('DD/MM/YYYY');
    };

    const formatDateTime = (date: any) => {
        if (!date) return 'Sin fecha';
        const dateValue = date?.date || date;
        return moment(dateValue).format('DD/MM/YYYY HH:mm');
    };

    const vehicle = refuel.vehicle;
    const user = refuel.user;
    const isSold = vehicle?.status === 'sold';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" containerClassName=' max-h-[90vh] max-w-[96vw] xl:max-w-[60vw] overflow-auto'>
            <div className="m-auto px-7 pt-6 pb-8">
                <div className="mb-7 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Detalle de Repostaje
                    </h3>
                    <ActionIcon
                        size="sm"
                        variant="text"
                        onClick={onClose}
                        className="p-0 text-gray-500 hover:!text-gray-900"
                    >
                        <HiOutlineX className="h-[18px] w-[18px]" />
                    </ActionIcon>
                </div>

                <div className="space-y-6">
                    {/* Información del Vehículo */}
                    <div className="rounded-lg border border-gray-200 p-4">
                        <h4 className="mb-3 font-medium text-gray-900">Información del Vehículo</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Matrícula</label>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm text-gray-900">
                                        {vehicle?.plateNumber || 'Sin matrícula'}
                                    </p>
                                    {isSold && (
                                        <Badge color="danger" size="sm">
                                            Vendido
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Marca y Modelo</label>
                                <p className="text-sm text-gray-900">
                                    {vehicle?.brand} {vehicle?.model}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Información del Usuario */}
                    <div className="rounded-lg border border-gray-200 p-4">
                        <h4 className="mb-3 font-medium text-gray-900">Usuario que Repostó</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Nombre</label>
                                <p className="text-sm text-gray-900">
                                    {user?.name} {user?.lastName}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Email</label>
                                <p className="text-sm text-gray-900">
                                    {user?.email || 'Sin email'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Información del Repostaje */}
                    <div className="rounded-lg border border-gray-200 p-4">
                        <h4 className="mb-3 font-medium text-gray-900">Detalles del Repostaje</h4>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Litros</label>
                                    <p className="text-lg font-semibold text-blue-600">
                                        {refuel.liters} L
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Costo Total</label>
                                    <p className="text-lg font-semibold text-green-600">
                                        {formatCurrency(refuel.totalCost)}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Fecha</label>
                                    <p className="text-sm text-gray-900">
                                        {formatDate(refuel.date)}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Kilómetros Totales</label>
                                    <p className="text-sm text-gray-900">
                                        {refuel.totalKilometers ? `${refuel.totalKilometers.toLocaleString()} km` : 'No registrado'}
                                    </p>
                                </div>
                            </div>

                            {refuel.photoCount !== undefined && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Fotos</label>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded text-xs ${refuel.photoCount > 0
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            📷 {refuel.photoCount} foto{refuel.photoCount !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {refuel.description && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Descripción</label>
                                    <div className="mt-1 rounded-md border border-gray-200 p-3">
                                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                            {refuel.description}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Información de Auditoría */}
                    <div className="rounded-lg border border-gray-200 p-4">
                        <h4 className="mb-3 font-medium text-gray-900">Información de Registro</h4>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Fecha de repostaje</label>
                                <p className="text-sm text-gray-900">
                                    {formatDateTime(refuel.date)}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Fecha de creación</label>
                                <p className="text-sm text-gray-900">
                                    {formatDateTime(refuel.createdAt)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Documentos e Imágenes */}
                    {refuel.photoCount > 0 && (
                        <div className="rounded-lg border border-gray-200 p-4">
                            <RefuelDocumentsViewer refuelId={refuel.id} columns={3} disableDelete={true} />
                        </div>
                    )}

                </div>

                <div className="mt-8 flex justify-end">
                    <button
                        onClick={onClose}
                        className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default RefuelDetailModal;